import { useEffect, useState, type ReactNode } from 'react'
import { Activity, Cpu, Gauge, Route } from 'lucide-react'
import { ErrorState, EmptyState, LoadingState } from './DataViz'
import {
  fetchBudgets,
  fetchInference,
  fetchModelRoutes,
  fetchRecentEvents,
  sendJson,
  type BudgetEffective,
} from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'

const BUDGET_LABELS: Record<string, { label: string; unit: string }> = {
  max_run_duration_s: { label: 'Max run duration', unit: 'seconds' },
  max_concurrent_runs: { label: 'Concurrent runs', unit: 'runs' },
  max_runs_per_day: { label: 'Runs per day', unit: 'runs' },
  max_run_cost_usd: { label: 'Cost per run', unit: 'USD' },
  max_stage_attempts: { label: 'Attempts per stage', unit: 'attempts' },
}

/**
 * Platform controls for Admin (AFOS-41): run budgets (editable by a named person
 * with projects.manage; the server records who set them), inference posture (no
 * key values ever), per-phase model routes, and recent project activity.
 */
export function PlatformAdmin() {
  const { user } = useAuth()
  const enabled = !!user
  const budgets = useAsync(fetchBudgets, enabled)
  const inference = useAsync(fetchInference, enabled)
  const routes = useAsync(fetchModelRoutes, enabled)
  const events = useAsync(() => fetchRecentEvents(25), enabled)
  const perms = new Set(user?.permissions ?? [])
  const canEdit = !!user?.is_super_admin || perms.has('projects.manage')

  return (
    <section id="budgets" aria-labelledby="platform-controls" className="space-y-4">
      <h2 id="platform-controls" className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
        Platform controls
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Run budgets" icon={<Gauge aria-hidden="true" className="h-4 w-4 text-[var(--color-amber)]" />}>
          {budgets.error && !budgets.data && <ErrorState message={budgets.error} onRetry={() => void budgets.reload()} />}
          {!budgets.data && !budgets.error && <LoadingState label="Loading budgets" />}
          {budgets.data && (
            <BudgetEditor
              budgets={budgets.data}
              canEdit={canEdit}
              onSaved={(next) => budgets.setData(next)}
            />
          )}
        </Panel>

        <Panel title="Inference" icon={<Cpu aria-hidden="true" className="h-4 w-4 text-[var(--color-cyan)]" />}>
          {inference.error && !inference.data && <ErrorState message={inference.error} onRetry={() => void inference.reload()} />}
          {!inference.data && !inference.error && <LoadingState label="Loading inference posture" />}
          {inference.data && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-[var(--color-mist)]">Mode</dt>
              <dd className="text-[var(--color-foam)]">{inference.data.mode}</dd>
              <dt className="text-[var(--color-mist)]">Region</dt>
              <dd className="text-[var(--color-foam)]">{inference.data.region || '—'}</dd>
              {Object.entries(inference.data.byok ?? {}).map(([vendor, info]) => (
                <div key={vendor} className="contents">
                  <dt className="text-[var(--color-mist)]">{vendor} key</dt>
                  <dd className="text-[var(--color-foam)]">
                    {info.configured ? `Configured${info.source ? ` (${info.source})` : ''}` : 'Not configured'}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p className="mt-3 text-xs text-[var(--color-mist)]">Key values are never shown or returned.</p>
        </Panel>

        <Panel title="Model routes by phase" icon={<Route aria-hidden="true" className="h-4 w-4 text-[var(--color-cyan)]" />}>
          {routes.error && !routes.data && <ErrorState message={routes.error} onRetry={() => void routes.reload()} />}
          {!routes.data && !routes.error && <LoadingState label="Loading model routes" />}
          {routes.data &&
            (routes.data.length === 0 ? (
              <EmptyState compact>No routes configured.</EmptyState>
            ) : (
              <div className="afos-scroll-x">
                <table className="w-full min-w-[28rem] text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-mist)]">
                      <th className="pb-2 font-semibold">Phase</th>
                      <th className="pb-2 font-semibold">Tier</th>
                      <th className="pb-2 font-semibold">Model</th>
                      <th className="pb-2 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.data.map((r) => (
                      <tr key={r.phase_id} className="border-t border-[var(--color-line)]">
                        <td className="py-1.5 text-[var(--color-foam)]">{r.phase_id}</td>
                        <td className="py-1.5 text-[var(--color-mist)]">{r.tier}</td>
                        <td className="py-1.5 font-mono text-xs text-[var(--color-foam)]">
                          {r.provider}/{r.model}
                        </td>
                        <td className="py-1.5 text-[var(--color-mist)]">{r.weight_source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          <p className="mt-3 text-xs text-[var(--color-mist)]">Routing is set in config and learning weights.</p>
        </Panel>

        <Panel title="Recent activity" icon={<Activity aria-hidden="true" className="h-4 w-4 text-[var(--color-cyan)]" />}>
          {events.error && !events.data && <ErrorState message={events.error} onRetry={() => void events.reload()} />}
          {!events.data && !events.error && <LoadingState label="Loading activity" />}
          {events.data &&
            (events.data.events.length === 0 ? (
              <EmptyState compact>No activity recorded for this project yet.</EmptyState>
            ) : (
              <ol className="max-h-72 space-y-1 overflow-auto text-sm">
                {[...events.data.events].reverse().map((e) => (
                  <li key={e.event_id} className="flex items-baseline justify-between gap-3 border-t border-[var(--color-line)] py-1.5">
                    <span className="min-w-0 truncate font-mono text-xs text-[var(--color-foam)]">{e.type}</span>
                    <span className="shrink-0 text-xs text-[var(--color-mist)]">
                      {e.actor ? `${e.actor} · ` : ''}
                      {new Date(e.occurred_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            ))}
        </Panel>
      </div>
    </section>
  )
}

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="glass rounded-3xl p-5">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-[var(--color-foam)]">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  )
}

function BudgetEditor({
  budgets,
  canEdit,
  onSaved,
}: {
  budgets: BudgetEffective
  canEdit: boolean
  onSaved: (next: BudgetEffective) => void
}) {
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    setDraft(Object.fromEntries(Object.entries(budgets.limits).map(([k, v]) => [k, String(v)])))
  }, [budgets])

  const changed = Object.entries(draft).filter(([k, v]) => String(budgets.limits[k]) !== v)

  async function save() {
    setSaving(true)
    setMessage(null)
    try {
      const limits = Object.fromEntries(changed.map(([k, v]) => [k, Number(v)]))
      const next = await sendJson<BudgetEffective>('/api/afos360/v1/budgets', 'PUT', { limits }, 'Could not save budgets')
      onSaved(next)
      setMessage('Saved. Recorded under your name.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not save budgets')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-[var(--color-mist)]">
        Source: {budgets.source === 'project' ? 'project override' : 'platform defaults'}
        {budgets.set_by ? ` · set by ${budgets.set_by}` : ''}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.keys(budgets.limits).map((key) => {
          const meta = BUDGET_LABELS[key] ?? { label: key, unit: '' }
          const id = `budget-${key}`
          return (
            <label key={key} htmlFor={id} className="block text-sm">
              <span className="text-[var(--color-mist)]">
                {meta.label} <span className="text-xs">({meta.unit})</span>
              </span>
              <input
                id={id}
                type="number"
                inputMode="decimal"
                disabled={!canEdit || saving}
                value={draft[key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-[var(--color-foam)] disabled:opacity-60"
              />
            </label>
          )
        })}
      </div>
      {canEdit ? (
        <button
          type="button"
          disabled={saving || changed.length === 0}
          onClick={() => void save()}
          className="rounded-full bg-[var(--color-amber)] px-4 py-2 text-sm font-semibold text-[var(--color-void)] disabled:opacity-50"
        >
          {saving ? 'Saving…' : changed.length ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'No changes'}
        </button>
      ) : (
        <p className="text-xs text-[var(--color-mist)]">Changing budgets needs the projects.manage permission.</p>
      )}
      {message && <p role="status" className="text-sm text-[var(--color-foam)]">{message}</p>}
    </div>
  )
}
